/**
 * CPF e CNPJ — normalização, máscara e validação (CRM-16).
 *
 * A tela só contava 11 ou 14 dígitos: dígito verificador errado, tipo
 * trocado (PF com CNPJ) e espaços passavam, e o erro só aparecia no boleto
 * (Asaas) ou na NF-e (eNotas), longe do cadastro e sem dizer onde corrigir.
 *
 * Mesmas regras de `core/documentos.py` no backend: grava-se só o número
 * (dígitos e, no CNPJ alfanumérico, letras maiúsculas), a máscara é
 * apresentação (aplicar no `onBlur` e na exibição) e a validação aceita o
 * texto com ou sem máscara.
 *
 * CNPJ alfanumérico (IN RFB 2.229/2024, emitido desde jul/2026): as 12
 * primeiras posições aceitam A-Z e 0-9, os 2 dígitos verificadores continuam
 * numéricos e o cálculo do DV usa o código do caractere menos 48 (para
 * dígito dá o próprio dígito). Por isso a normalização NUNCA descarta letra:
 * "12.ABC.345/01DE-35" é um CNPJ válido, e sem as letras seria outro número.
 */

export const PESSOA_FISICA = "PF";
export const PESSOA_JURIDICA = "PJ";

const DIGITOS_CPF = 11;
const DIGITOS_CNPJ = 14;
const MODULO_DO_DV = 11;
// Resto abaixo disto dá DV 0 (regra da Receita para CPF e CNPJ).
const RESTO_MINIMO_DO_DV = 2;
// Valor do caractere no cálculo do DV: código - 48 ("0" vale 0, "A" vale 17).
const BASE_DO_CARACTERE = "0".charCodeAt(0);

// Pesos do módulo 11 de cada dígito verificador.
const PESOS_CPF_DV1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CPF_DV2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const SO_DIGITOS = /^\d+$/;
// 12 posições alfanuméricas + 2 dígitos verificadores numéricos.
const FORMATO_CNPJ = /^[0-9A-Z]{12}\d{2}$/;

// Mensagens IGUAIS às de `core/documentos.py` (o 400 do backend).
export const MSG_CPF_TAMANHO = "CPF deve ter 11 dígitos.";
export const MSG_CPF_INVALIDO = "CPF inválido.";
export const MSG_CNPJ_TAMANHO = "CNPJ deve ter 14 dígitos.";
export const MSG_CNPJ_INVALIDO = "CNPJ inválido.";
export const MSG_DOCUMENTO_TAMANHO = "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).";

/** Texto digitado -> só o número: dígitos e letras em maiúscula ("" quando vazio). */
export function normalizarDocumento(valor) {
  return String(valor ?? "").toUpperCase().replace(/[^0-9A-Z]/g, "");
}

function digitoVerificador(numero, pesos) {
  const soma = pesos.reduce(
    (acc, peso, i) => acc + (numero.charCodeAt(i) - BASE_DO_CARACTERE) * peso,
    0,
  );
  const resto = soma % MODULO_DO_DV;
  return resto < RESTO_MINIMO_DO_DV ? 0 : MODULO_DO_DV - resto;
}

function dvsConferem(numero, pesosDv1, pesosDv2) {
  const tamanho = numero.length;
  // "111.111.111-11" fecha a conta do módulo 11, mas não existe.
  if (/^(.)\1*$/.test(numero)) return false;
  return (
    digitoVerificador(numero, pesosDv1) === Number(numero[tamanho - 2])
    && digitoVerificador(numero, pesosDv2) === Number(numero[tamanho - 1])
  );
}

/** "" quando é CPF válido; senão a mensagem do backend. */
function erroDoCpf(valor) {
  const numero = normalizarDocumento(valor);
  if (numero.length !== DIGITOS_CPF) return MSG_CPF_TAMANHO;
  if (!SO_DIGITOS.test(numero) || !dvsConferem(numero, PESOS_CPF_DV1, PESOS_CPF_DV2)) {
    return MSG_CPF_INVALIDO;
  }
  return "";
}

/** "" quando é CNPJ válido (numérico ou alfanumérico); senão a mensagem do backend. */
function erroDoCnpj(valor) {
  const numero = normalizarDocumento(valor);
  if (numero.length !== DIGITOS_CNPJ) return MSG_CNPJ_TAMANHO;
  if (!FORMATO_CNPJ.test(numero) || !dvsConferem(numero, PESOS_CNPJ_DV1, PESOS_CNPJ_DV2)) {
    return MSG_CNPJ_INVALIDO;
  }
  return "";
}

export function validarCpf(valor) {
  return erroDoCpf(valor) === "";
}

export function validarCnpj(valor) {
  return erroDoCnpj(valor) === "";
}

function exigirTipoConhecido(tipoPessoa) {
  if (tipoPessoa && tipoPessoa !== PESSOA_FISICA && tipoPessoa !== PESSOA_JURIDICA) {
    // Erro de programação, não de digitação: falha alto, como o backend.
    throw new Error(`Tipo de pessoa desconhecido: ${tipoPessoa}`);
  }
}

/**
 * PF exige CPF válido e PJ exige CNPJ válido — é o tipo trocado que o
 * cadastro deixava passar. Sem tipo (campo CPF/CNPJ do formulário público,
 * onde quem responde não diz o tipo), aceita qualquer um dos dois.
 * Espelha `validar_documento` de `core/documentos.py`.
 */
export function validarDocumento(valor, tipoPessoa) {
  exigirTipoConhecido(tipoPessoa);
  if (tipoPessoa === PESSOA_FISICA) return validarCpf(valor);
  if (tipoPessoa === PESSOA_JURIDICA) return validarCnpj(valor);
  return validarCpf(valor) || validarCnpj(valor);
}

/**
 * Mensagem de erro do documento para mostrar no campo, antes de enviar:
 * "" quando está ok ou vazio (documento é opcional no cadastro), senão o
 * mesmo texto que o 400 do backend devolveria.
 */
export function erroDoDocumento(valor, tipoPessoa) {
  exigirTipoConhecido(tipoPessoa);
  const numero = normalizarDocumento(valor);
  if (!numero) return "";
  if (tipoPessoa === PESSOA_FISICA) return erroDoCpf(numero);
  if (tipoPessoa === PESSOA_JURIDICA) return erroDoCnpj(numero);
  if (numero.length === DIGITOS_CPF) return erroDoCpf(numero);
  if (numero.length === DIGITOS_CNPJ) return erroDoCnpj(numero);
  return MSG_DOCUMENTO_TAMANHO;
}

/**
 * "12345678909" -> "123.456.789-09"; "11222333000181" -> "11.222.333/0001-81";
 * "12abc34501de35" -> "12.ABC.345/01DE-35".
 *
 * Quando o número não fecha com o tipo, devolve o texto como veio: vestir de
 * CNPJ o que foi digitado num cadastro PF esconderia o erro.
 */
export function mascaraDocumento(valor, tipoPessoa) {
  const texto = String(valor ?? "");
  const d = normalizarDocumento(texto);
  if (d.length === DIGITOS_CPF && SO_DIGITOS.test(d) && tipoPessoa !== PESSOA_JURIDICA) {
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  if (FORMATO_CNPJ.test(d) && tipoPessoa !== PESSOA_FISICA) {
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }
  return texto;
}
